// Authentication utilities
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { auth, googleProvider, functions } from './config'

/**
 * Check if user has whitelisted custom claim
 * @param {User} user - Firebase user object
 * @returns {boolean} - True if user is whitelisted
 */
export const isUserWhitelisted = (user) => {
  if (!user) return false
  // Custom claims are in user's ID token
  // We need to get the ID token result to access custom claims
  return user.whitelisted === true
}

/**
 * Check if user has admin custom claim
 * @param {User} user - Firebase user object
 * @param {boolean} forceRefresh - Force token refresh before checking
 * @returns {Promise<boolean>} - True if user is admin
 */
export const isUserAdmin = async (user, forceRefresh = false) => {
  if (!user) return false
  try {
    // Force refresh if requested to get latest claims
    if (forceRefresh) {
      await user.getIdToken(true)
    }
    const tokenResult = await user.getIdTokenResult(forceRefresh)
    return tokenResult.claims.admin === true
  } catch (error) {
    console.error('Error checking admin status:', error)
    return false
  }
}

/**
 * Check if user has editor custom claim
 * @param {User} user - Firebase user object
 * @param {boolean} forceRefresh - Force token refresh before checking
 * @returns {Promise<boolean>} - True if user is editor
 */
export const isUserEditor = async (user, forceRefresh = false) => {
  if (!user) return false
  try {
    // Force refresh if requested to get latest claims
    if (forceRefresh) {
      await user.getIdToken(true)
    }
    const tokenResult = await user.getIdTokenResult(forceRefresh)
    return tokenResult.claims.editor === true
  } catch (error) {
    console.error('Error checking editor status:', error)
    return false
  }
}

/**
 * Check if user has sdr custom claim
 * @param {User} user - Firebase user object
 * @param {boolean} forceRefresh - Force token refresh before checking
 * @returns {Promise<boolean>} - True if user has sdr claim
 */
export const isUserSdr = async (user, forceRefresh = false) => {
  if (!user) return false
  try {
    if (forceRefresh) {
      await user.getIdToken(true)
    }
    const tokenResult = await user.getIdTokenResult(forceRefresh)
    return tokenResult.claims.sdr === true
  } catch (error) {
    console.error('Error checking sdr status:', error)
    return false
  }
}

/**
 * Refresh user's ID token to get updated custom claims
 * @param {User} user - Firebase user object
 * @returns {Promise<void>}
 */
export const refreshUserToken = async (user) => {
  if (!user) return
  try {
    await user.getIdToken(true) // Force refresh
  } catch (error) {
    console.error('Error refreshing token:', error)
  }
}

/**
 * Check whitelist status via Cloud Function
 * This will refresh custom claims and verify access
 * @returns {Promise<Object>} - Status object
 */
export const checkWhitelistStatus = async () => {
  try {
    const checkStatus = httpsCallable(functions, 'checkWhitelistStatus')
    const result = await checkStatus()
    return result.data
  } catch (error) {
    console.error('Error checking status:', error)
    // Handle specific error codes
    if (error.code === 'functions/failed-precondition') {
      throw new Error('App Check verification failed. Please ensure App Check is properly configured.')
    }
    if (error.code === 'functions/unauthenticated') {
      throw new Error('You must be signed in to check status.')
    }
    if (error.code === 'functions/permission-denied') {
      throw new Error('Access denied. Your email address is not authorized.')
    }
    if (error.code === 'functions/internal') {
      throw new Error('An internal error occurred. Please try again later.')
    }
    // Re-throw with original message if available
    throw error
  }
}

/**
 * Sign in with Google
 * After sign-in, checks whitelist status via Cloud Function
 * @returns {Promise<UserCredential>}
 */
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider)
    const user = result.user
    
    // Wait a moment for the auth trigger to process
    await new Promise(resolve => setTimeout(resolve, 1500))
    
    // Refresh token to get custom claims
    try {
      await refreshUserToken(user)
    } catch (tokenError) {
      console.error('Error refreshing token:', tokenError)
      // If user was deleted by the auth trigger, token refresh will fail
      if (tokenError.code === 'auth/user-not-found' || tokenError.code === 'auth/user-disabled') {
        await firebaseSignOut(auth)
        throw new Error('Access denied. Your email address is not authorized.')
      }
    }
    
    // Check whitelist status via Cloud Function
    try {
      await checkWhitelistStatus()
    } catch (error) {
      // If check fails (user not whitelisted), they'll be signed out
      // The Cloud Function will delete their account
      console.error('Whitelist check failed:', error)
      // Sign out the user if they're still signed in
      try {
        await firebaseSignOut(auth)
      } catch (signOutError) {
        // User might already be signed out
        console.log('User already signed out or deleted')
      }
      throw error
    }
    
    return user
  } catch (error) {
    console.error('Error signing in with Google:', error)
    // If user was deleted, sign out
    if (error.code === 'auth/user-not-found' || error.code === 'auth/user-disabled' || error.code === 'auth/internal-error') {
      try {
        await firebaseSignOut(auth)
      } catch (signOutError) {
        // User might already be signed out
      }
      // Provide a more user-friendly error message
      if (error.message && !error.message.includes('Access denied')) {
        throw new Error('Access denied. Your email address is not authorized.')
      }
    }
    throw error
  }
}

/**
 * Sign out current user
 */
export const signOut = async () => {
  try {
    await firebaseSignOut(auth)
  } catch (error) {
    console.error('Error signing out:', error)
    throw error
  }
}

/**
 * Get current user
 * @returns {Promise<User | null>}
 */
export const getCurrentUser = () => {
  return new Promise((resolve) => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      unsubscribe()
      resolve(user)
    })
  })
}

/**
 * Subscribe to auth state changes
 * @param {Function} callback - Callback function that receives the user
 * @returns {Function} Unsubscribe function
 */
export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback)
}

